import { useRef } from "react"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"

import {
    RING_GAP_CENTER,
    RING_GAP_HALF,
    RING_OBSTACLE_BAND,
    RING_OBSTACLE_HEIGHT,
    RING_OBSTACLE_RADIUS,
} from "../../lib/domeConstants"

// 2026-05-05 v6 — Aro obstáculo cyan al ras del piso del Domo.
//
// El Tripulante NO puede atravesarlo caminando: LocalAvatar detecta
// el cruce y lo detiene en el borde con animKey "idle". Para cruzar
// hay que saltar (S): si la altura del cuerpo durante el arco supera
// RING_OBSTACLE_HEIGHT, el chequeo se ignora y el avatar pasa.
//
// Visualmente lo representamos con DOS toroides concéntricos finos
// + un cilindro de cristal entre ellos (banda emisiva semi-translúcida)
// para que se sienta "muralla holográfica" y no un simple aro
// pintado en el piso. El conjunto pulsa con ritmo sutil.
//
// 2026-05-05 v7 — Apertura física en el aro. La pared deja una brecha
// de ~1/8 (45°) centrada en RING_GAP_CENTER (eje +X por default), por
// donde el Tripulante puede cruzar caminando sin saltar. Las
// geometrías se construyen con thetaStart/thetaLength para que el
// hueco sea coherente entre lo visual y lo físico (la lógica de
// colisión vive en `pathCrossesRing` y comparte las constantes).

// Convención del thetaStart en three.js para ringGeometry y
// cylinderGeometry: el ángulo 0 corresponde al eje +X (tras la
// rotación -π/2 del ring para acostarlo, el +X del mundo permanece).
// Para centrar el gap en RING_GAP_CENTER:
//   thetaStart = RING_GAP_CENTER + RING_GAP_HALF
//   thetaLength = 2π - 2·RING_GAP_HALF  (el complemento del gap)
const THETA_START = RING_GAP_CENTER + RING_GAP_HALF
const THETA_LENGTH = Math.PI * 2 - RING_GAP_HALF * 2

export function ObstacleRing() {
    const outerRef = useRef<THREE.Mesh>(null)
    const innerRef = useRef<THREE.Mesh>(null)

    useFrame((state) => {
        const t = state.clock.elapsedTime
        if (outerRef.current) {
            const m = outerRef.current.material as THREE.MeshStandardMaterial
            m.emissiveIntensity = 1.4 + Math.sin(t * 1.4 + 0.6) * 0.4
        }
        if (innerRef.current) {
            const m = innerRef.current.material as THREE.MeshStandardMaterial
            m.emissiveIntensity = 1.0 + Math.sin(t * 1.4) * 0.3
        }
    })

    return (
        <group>
            {/* Aro inferior — borde cyan brillante en el piso. */}
            <mesh
                ref={outerRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.02, 0]}
            >
                <ringGeometry
                    args={[
                        RING_OBSTACLE_RADIUS - RING_OBSTACLE_BAND,
                        RING_OBSTACLE_RADIUS,
                        96,
                        1,
                        THETA_START,
                        THETA_LENGTH,
                    ]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#00FFFF"
                    emissiveIntensity={1.5}
                    transparent
                    opacity={0.95}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* 2026-05-06 — Pared cilíndrica removida. Generaba un
                "gel translúcido" que se proyectaba al piso a través
                del reflector y dejaba una sombra visible justo en
                la apertura del aro. Ahora la lectura de muralla
                holográfica viene solo del aro inferior + el aro
                superior; la apertura queda totalmente vacía. */}

            {/* Aro superior — marco cyan en el tope del obstáculo. */}
            <mesh
                ref={innerRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, RING_OBSTACLE_HEIGHT, 0]}
            >
                <ringGeometry
                    args={[
                        RING_OBSTACLE_RADIUS - RING_OBSTACLE_BAND * 0.6,
                        RING_OBSTACLE_RADIUS,
                        96,
                        1,
                        THETA_START,
                        THETA_LENGTH,
                    ]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#7FF6FF"
                    emissiveIntensity={1.2}
                    transparent
                    opacity={0.85}
                    side={THREE.DoubleSide}
                />
            </mesh>
        </group>
    )
}
